import _ from 'lodash';
import { useEffect, useState } from 'react';

import { apiGetNumberSpeechAudio } from 'core/api';
import { isAbortError, isNotAllowedError } from 'core/helpers/browser';
import { useStableCallback } from 'core/hooks/useStableCallback';
import { useMessageModal } from 'core/ui/MessageModal';

let prevAudio: HTMLAudioElement | null = null;
let prevUtterance: SpeechSynthesisUtterance | null = null;
/** iPhone supports only Safari Webkit, which doesn't support async audios.  */
const isIphone = /iPhone/.test(navigator.userAgent);

export default function useSynthesis(lang: TLang, value: number, next: number) {
  const [isAudioLoading, setIsLoading] = useState(false);
  const { showError } = useMessageModal();

  const replay = isIphone
    ? useStableCallback(() => {
        if (prevUtterance) {
          prevUtterance.volume = 0;
        }
        const utterance = getUtterance(lang, value);
        speechSynthesis.speak(getUtterance(lang, value));
        prevUtterance = utterance;
      })
    : useStableCallback(async () => {
        speechSynthesis.cancel();
        if (prevAudio) {
          prevAudio.pause();
          prevAudio.currentTime = 0;
        }

        try {
          setIsLoading(true);

          const result = await Promise.race([
            getAudio(lang, value).catch((err: unknown) => {
              console.error(err);
              return waitForUtteranceAndFail();
            }),
            getUtteranceAsync(lang, value),
          ]);

          if (result instanceof HTMLAudioElement) {
            result
              .play()
              .catch(
                (e: unknown) =>
                  !isAbortError(e) && !isNotAllowedError(e) && showError(e)
              );
            prevAudio = result;
          } else {
            speechSynthesis.speak(result);
          }
        } catch (error) {
          showError(error);
        } finally {
          setIsLoading(false);
        }
      });

  useEffect(() => {
    if (value) replay();
  }, [value]);

  useEffect(() => {
    if (next) {
      getAudio(lang, next).catch(console.error);
    }
  }, [lang, next]);

  useEffect(() => {
    return () => {
      cache.clear();
      prevAudio = null;
    };
  }, []);

  return { isAudioLoading, replay };
}

const getUtterance = (
  lang: TLang,
  number: number
): SpeechSynthesisUtterance => {
  const voices = speechSynthesis
    // first call returns an empty array.
    .getVoices()
    .filter(l => l.lang.startsWith(lang));

  const utterance = new SpeechSynthesisUtterance(' ' + String(number));
  utterance.lang = lang;
  if (voices.length) {
    utterance.voice =
      // try to use the best voice
      voices.find(v => v.voiceURI === BEST_VOICES[lang]) ??
      // or use any
      orDie(_.sample(voices));
  }

  return utterance;
};

const getUtteranceAsync = async (
  lang: TLang,
  number: number
): Promise<SpeechSynthesisUtterance> => {
  const utterance = getUtterance(lang, number);
  // give the backend version a chance
  await new Promise(r => setTimeout(r, UTTERANCE_DELAY));

  return utterance;
};

const BEST_VOICES: Partial<Record<TLang, string>> = {
  [TLang.de]: 'Google Deutsch',
};

// Do not play SpeechSynthesisUtterance too early. Use it only as a fallback.
const UTTERANCE_DELAY = 3_000;

// Do not reject the audio flow immediately to make Promise.race able to use the
// fallback way.
const waitForUtteranceAndFail = () =>
  new Promise<HTMLAudioElement>((resolve, reject) =>
    setTimeout(reject, UTTERANCE_DELAY * 3)
  );

const cache = new Map<string, Promise<HTMLAudioElement>>();

const getAudio = (lang: TLang, number: number): Promise<HTMLAudioElement> => {
  const cacheKey = `${lang}:${number}`;
  if (!cache.has(cacheKey)) {
    cache.set(cacheKey, _getAudio(lang, number));
  }

  return orDie(cache.get(cacheKey));
};

const _getAudio = (lang: TLang, number: number): Promise<HTMLAudioElement> =>
  new Promise<HTMLAudioElement>((resolve, reject) => {
    const $audio = new Audio(apiGetNumberSpeechAudio(lang, number));

    const onPlay = () => {
      $audio.removeEventListener('canplaythrough', onPlay);
      $audio.pause();
      $audio.currentTime = 0;
      $audio.volume = 1;
      resolve($audio);
    };
    $audio.addEventListener('canplaythrough', onPlay);

    $audio.onerror = err => {
      const msg = String(
        typeof err === 'object' && 'message' in err ? err.message : err
      );
      reject(
        new Error(
          `Couldn't download a number-audio for ${lang}:${number}. Error: ${msg}`
        )
      );
    };

    $audio.volume = 0.01; // just in case.
    void $audio.play(); // trigger loading.
  });
